import { z } from "zod";
import {
  NOTIFICATION_FREQUENCIES,
  isNotificationFrequency,
  type NotificationFrequency,
} from "./notification-schedule";

/**
 * Business-member email notification preferences.
 *
 * Stored per member in Firestore:
 *   Businesses/{businessId}/notificationSettings/{uid}_{notificationType}_email
 *
 * Missing document ⇒ enabled = true (opt-out model).
 *
 * Each doc carries both `enabled` (the original boolean) and `frequency` (the
 * cadence). `frequency` is authoritative; `enabled` is kept in sync on every
 * write as `frequency !== "off"` so that any reader still on the boolean —
 * including docs written before this field existed — stays correct.
 */

export const BUSINESS_NOTIFICATION_TYPES = [
  "submitted_lead",
  "submitted_leads_weekly_summary",
  "unread_chat_reminder",
  "pulse_check_reminder",
  "intelligence_insights_digest",
] as const;

export type BusinessNotificationType =
  (typeof BUSINESS_NOTIFICATION_TYPES)[number];

export const NOTIFICATION_CHANNELS = ["email", "inapp"] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export interface BusinessNotificationCatalogEntry {
  type: BusinessNotificationType;
  label: string;
  description: string;
  supportedChannels: readonly NotificationChannel[];
  showInSettings: boolean;
  category: string;
  /**
   * What a missing preference doc means. Omitted ⇒ true (the historical
   * opt-out model). Set false for opt-in notifications like the AI digest.
   */
  defaultEnabled?: boolean;
  /**
   * Cadences this type can actually be delivered on. Always includes "off".
   * Derived from what the senders support, not a uniform set — an event-driven
   * notification can be "instant", a weekly rollup cannot.
   */
  allowedFrequencies: readonly NotificationFrequency[];
  /** Cadence used when the member has never chosen one. */
  defaultFrequency: NotificationFrequency;
}

export const BUSINESS_NOTIFICATION_CATALOG: readonly BusinessNotificationCatalogEntry[] =
  [
    {
      type: "submitted_lead",
      label: "Nya leads",
      description:
        "E-post när en ny lead skickas in via ditt lead-formulär.",
      supportedChannels: ["email"],
      showInSettings: true,
      category: "leads",
      allowedFrequencies: ["off", "instant", "daily"],
      defaultFrequency: "instant",
    },
    {
      type: "submitted_leads_weekly_summary",
      label: "Veckovis leadsammanfattning",
      description:
        "E-post en gång i veckan med en sammanfattning av nya inskickade leads.",
      supportedChannels: ["email"],
      showInSettings: true,
      category: "leads",
      // Only "off | weekly", so the settings row stays a plain switch — see
      // `isBooleanNotification`.
      allowedFrequencies: ["off", "weekly"],
      defaultFrequency: "weekly",
    },
    {
      type: "unread_chat_reminder",
      label: "Olästa chattmeddelanden",
      description:
        "Daglig påminnelse om olästa meddelanden från kandidater.",
      supportedChannels: ["email"],
      showInSettings: true,
      category: "chat",
      allowedFrequencies: ["off", "daily", "weekly"],
      defaultFrequency: "daily",
    },
    {
      type: "pulse_check_reminder",
      label: "Pulse-påminnelse",
      description:
        "Påminnelse att göra Pulse check-in för ditt team när en period är avslutad.",
      supportedChannels: ["email", "inapp"],
      showInSettings: true,
      category: "intelligence",
      allowedFrequencies: ["off", "daily", "weekly"],
      defaultFrequency: "daily",
    },
    {
      type: "intelligence_insights_digest",
      label: "Att uppmärksamma",
      description:
        "Morgonrapport med nya insikter och förslag att ta ställning till.",
      // Was email-only and opt-in — the only opt-in entry in an otherwise
      // opt-out catalog, which is why nobody ever received it.
      supportedChannels: ["email", "inapp"],
      showInSettings: true,
      category: "intelligence",
      allowedFrequencies: ["off", "daily", "weekly"],
      defaultFrequency: "daily",
    },
  ] as const;

export function getNotificationCatalogEntry(
  type: BusinessNotificationType,
): BusinessNotificationCatalogEntry | undefined {
  return BUSINESS_NOTIFICATION_CATALOG.find((e) => e.type === type);
}

/**
 * True when the type offers exactly one cadence besides "off" — there is no
 * choice to make, so the UI renders a switch rather than a frequency picker.
 */
export function isBooleanNotification(
  entry: BusinessNotificationCatalogEntry,
): boolean {
  return entry.allowedFrequencies.filter((f) => f !== "off").length <= 1;
}

export interface BusinessNotificationPref {
  userId: string;
  businessId: string;
  notificationType: BusinessNotificationType;
  channel: NotificationChannel;
  enabled: boolean;
  /** Absent on docs written before cadence existed — see `resolveNotificationFrequency`. */
  frequency?: NotificationFrequency;
  updatedAt: unknown;
}

/** The subset of a stored pref doc that gating cares about. */
export type StoredNotificationPref = {
  enabled?: unknown;
  frequency?: unknown;
};

/**
 * Cadence for a member, given whatever is stored (possibly nothing).
 *
 * This is the whole migration story for existing docs: a pre-cadence doc has
 * only `enabled`, so `enabled: false` reads as "off" and `enabled: true` reads
 * as the catalog default. Nobody who had a notification switched off starts
 * receiving it again.
 */
export function resolveNotificationFrequency(
  type: BusinessNotificationType,
  pref?: StoredNotificationPref | null,
): NotificationFrequency {
  const entry = getNotificationCatalogEntry(type);
  if (entry == null) return "off";

  const stored = pref?.frequency;
  if (isNotificationFrequency(stored) && entry.allowedFrequencies.includes(stored)) {
    return stored;
  }

  if (pref?.enabled === false) return "off";

  if (pref == null) {
    return entry.defaultEnabled === false ? "off" : entry.defaultFrequency;
  }

  return entry.defaultFrequency;
}

export interface BusinessNotificationSettingView {
  notificationType: BusinessNotificationType;
  channel: NotificationChannel;
  enabled: boolean;
  frequency: NotificationFrequency;
  allowedFrequencies: readonly NotificationFrequency[];
  label: string;
  description: string;
  category: string;
}

export function makeNotificationSettingDocId(
  uid: string,
  notificationType: BusinessNotificationType,
  channel: NotificationChannel = "email",
): string {
  return `${uid}_${notificationType}_${channel}`;
}

export function mergeCatalogWithPrefs(
  prefs: (Pick<BusinessNotificationPref, "notificationType" | "channel"> &
    StoredNotificationPref)[],
): BusinessNotificationSettingView[] {
  const prefMap = new Map(
    prefs.map((p) => [`${p.notificationType}_${p.channel}`, p]),
  );

  return BUSINESS_NOTIFICATION_CATALOG.filter((e) => e.showInSettings).flatMap(
    (entry) =>
      entry.supportedChannels.map((channel) => {
        const stored = prefMap.get(`${entry.type}_${channel}`) ?? null;
        const frequency = resolveNotificationFrequency(entry.type, stored);
        return {
          notificationType: entry.type,
          channel,
          enabled: frequency !== "off",
          frequency,
          allowedFrequencies: entry.allowedFrequencies,
          label: entry.label,
          description: entry.description,
          category: entry.category,
        };
      }),
  );
}

export const BusinessNotificationGetAllInput = z.object({
  businessId: z.string().min(1),
});

/**
 * Accepts either a cadence or the legacy boolean, so an older client (or the
 * admin dashboard's mute-all) keeps working. Exactly one must be supplied;
 * `enabled` is mapped onto the type's default cadence.
 */
export const BusinessNotificationUpdateInput = z
  .object({
    businessId: z.string().min(1),
    notificationType: z.enum(BUSINESS_NOTIFICATION_TYPES),
    channel: z.enum(NOTIFICATION_CHANNELS),
    enabled: z.boolean().optional(),
    frequency: z.enum(NOTIFICATION_FREQUENCIES).optional(),
  })
  .superRefine((input, ctx) => {
    if (input.enabled == null && input.frequency == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Either `enabled` or `frequency` must be provided",
        path: ["frequency"],
      });
      return;
    }
    if (input.frequency == null) return;

    const entry = getNotificationCatalogEntry(input.notificationType);
    if (entry != null && !entry.allowedFrequencies.includes(input.frequency)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Frequency "${input.frequency}" is not allowed for ${input.notificationType}`,
        path: ["frequency"],
      });
    }
  });

/** Turns the two accepted input shapes into the single cadence we persist. */
export function resolveUpdateFrequency(input: {
  notificationType: BusinessNotificationType;
  enabled?: boolean;
  frequency?: NotificationFrequency;
}): NotificationFrequency {
  if (input.frequency != null) return input.frequency;
  if (input.enabled === false) return "off";
  return (
    getNotificationCatalogEntry(input.notificationType)?.defaultFrequency ??
    "daily"
  );
}
